/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { css } from 'styled-components';
import { generatePatternStyles } from '@web-stories-wp/patterns';

/**
 * Internal dependencies
 */
import {
  getListMarkerGlyph,
  getListMarkerShape,
  getNormalizedTextListStyle,
  getOrderedListMarkerColumnWidth,
  hasSemanticList,
} from './utils/listStyle';
import getListMarkerColor from './utils/getListMarkerColor';
import { generateFontFamily } from './utils/generateFontFamily';
import getListMarkerBaselineOffset from './utils/getListMarkerBaselineOffset';
import measureTextWidth from './utils/measureTextWidth';

const toCssValue = (value) =>
  typeof value === 'number' ? `${value}px` : value;

// Draft renders list depths as classes on flat sibling <li> elements, while
// saved content uses genuinely nested lists. Cover Draft's supported depth
// classes so both structures reserve the same horizontal space.
const DRAFT_LIST_DEPTHS = [0, 1, 2, 3, 4];

function getOrderedListItemCounts(content) {
  if (typeof DOMParser === 'undefined') {
    return [];
  }

  const { body } = new DOMParser().parseFromString(content, 'text/html');
  return Array.from(body.querySelectorAll('ol')).map(
    (list) =>
      Array.from(list.children).filter((child) => child.tagName === 'LI').length
  );
}

function getMarkerFallbackWidth(glyph, markerSize) {
  const digitCount = Math.max(1, Array.from(glyph.replace(/\.$/, '')).length);
  return Math.max(markerSize, markerSize * (digitCount * 0.6 + 0.3));
}

export const elementWithTextList = css`
  ${({
    $hasList,
    $matchSvgListMetrics,
    element,
    dataToEditorX,
    dataToEditorY,
  }) => {
    const shouldRenderListStyles =
      typeof $hasList === 'boolean'
        ? $hasList
        : hasSemanticList(element?.content);

    if (!element || !shouldRenderListStyles) {
      return null;
    }

    const convertX = (value) => (dataToEditorX ? dataToEditorX(value) : value);
    const toX = (value) => toCssValue(convertX(value));
    const toY = (value) =>
      toCssValue(dataToEditorY ? dataToEditorY(value) : value);
    const listStyle = getNormalizedTextListStyle(
      element.listStyle,
      element.fontSize
    );
    const markerSize = listStyle.marker.size || element.fontSize;
    const markerColor = generatePatternStyles(
      getListMarkerColor(element),
      'color'
    ).color;
    const markerGlyph = getListMarkerGlyph(listStyle, 'unordered')
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"');
    const markerFontSize = dataToEditorY
      ? dataToEditorY(markerSize)
      : markerSize;
    const markerShape = getListMarkerShape(
      listStyle,
      element.fontSize,
      element.lineHeight
    );
    const glyphBaselineOffset = getListMarkerBaselineOffset({
      fontMetrics: element.font?.metrics,
      fontSize: element.fontSize,
      lineHeight: element.lineHeight,
      markerSize,
      markerBaselineShift: listStyle.marker.baselineShift,
    });
    const unorderedMarkerWidth = markerShape
      ? convertX(markerShape.size)
      : measureTextWidth(markerGlyph, {
          fallbackWidth: markerFontSize,
          fontFamily: generateFontFamily(element.font),
          fontSize: markerFontSize,
          fontStyle: element.fontStyle,
          fontWeight: element.fontWeight,
        });
    const orderedMarkerFallbackWidth = convertX(markerSize);
    const orderedMarkerWidth = Math.max(
      orderedMarkerFallbackWidth,
      ...getOrderedListItemCounts(element.content).map((itemCount) =>
        getOrderedListMarkerColumnWidth(
          itemCount,
          (glyph) =>
            measureTextWidth(glyph, {
              fallbackWidth: getMarkerFallbackWidth(
                glyph,
                orderedMarkerFallbackWidth
              ),
              fontFamily: generateFontFamily(element.font),
              fontSize: markerFontSize,
              fontStyle: element.fontStyle,
              fontWeight: element.fontWeight,
            }),
          orderedMarkerFallbackWidth
        )
      )
    );
    const unorderedDraftDepthStep =
      convertX(listStyle.nestedIndent) +
      unorderedMarkerWidth +
      convertX(listStyle.markerGap);
    const orderedDraftDepthStep =
      convertX(listStyle.nestedIndent) +
      orderedMarkerWidth +
      convertX(listStyle.markerGap);

    return css`
      p {
        margin: 0;
        padding: 0;
      }

      ul,
      ol {
        list-style: none;
        margin: 0;
        padding: 0;
        padding-inline-start: ${toX(listStyle.indent)};
      }

      ul ul,
      ul ol,
      ol ul,
      ol ol {
        margin-block-start: ${toY(listStyle.itemSpacing)};
        padding-inline-start: ${toX(listStyle.nestedIndent)};
      }

      li {
        margin: 0;
        padding: 0;
        padding-inline-start: calc(
          ${toCssValue(orderedMarkerWidth)} + ${toX(listStyle.markerGap)}
        );
        position: relative;
      }

      ul > li {
        padding-inline-start: calc(
          ${toCssValue(unorderedMarkerWidth)} + ${toX(listStyle.markerGap)}
        );
      }

      li + li {
        margin-block-start: ${toY(listStyle.itemSpacing)};
      }

      ${DRAFT_LIST_DEPTHS.map((depth) => {
        const unorderedOffset = toCssValue(depth * unorderedDraftDepthStep);
        const orderedOffset = toCssValue(depth * orderedDraftDepthStep);

        return css`
          li.public-DraftStyleDefault-unorderedListItem.public-DraftStyleDefault-depth${depth}.public-DraftStyleDefault-listLTR {
            margin-left: ${unorderedOffset};
            margin-right: 0;
          }

          li.public-DraftStyleDefault-unorderedListItem.public-DraftStyleDefault-depth${depth}.public-DraftStyleDefault-listRTL {
            margin-left: 0;
            margin-right: ${unorderedOffset};
          }

          li.public-DraftStyleDefault-orderedListItem.public-DraftStyleDefault-depth${depth}.public-DraftStyleDefault-listLTR {
            margin-left: ${orderedOffset};
            margin-right: 0;
          }

          li.public-DraftStyleDefault-orderedListItem.public-DraftStyleDefault-depth${depth}.public-DraftStyleDefault-listRTL {
            margin-left: 0;
            margin-right: ${orderedOffset};
          }
        `;
      })}

      ${$matchSvgListMetrics &&
      css`
        /* SVG includes the marker font in the first line's metrics. Reproduce
         * that contribution without adding text or a second visible marker. */
        li > .public-DraftStyleDefault-block::before,
        li > [data-caret-list-marker]::before {
          content: '';
          font-family: ${generateFontFamily(element.font)};
          font-size: ${toY(markerSize)};
          font-style: ${element.fontStyle || 'normal'};
          font-weight: ${element.fontWeight || 400};
          letter-spacing: 0;
          line-height: ${element.lineHeight};
        }
      `}

      li::before {
        background: none;
        color: ${markerColor};
        content: '${markerGlyph}';
        font-size: ${toY(markerSize)};
        inset-block-start: ${toY(glyphBaselineOffset)};
        inset-inline-start: 0;
        letter-spacing: normal;
        line-height: inherit;
        position: absolute;
        text-align: start;
        -webkit-text-fill-color: ${markerColor};
        width: ${toCssValue(orderedMarkerWidth)};
      }

      ul > li::before {
        inset-inline-start: ${toX(
          listStyle.marker.preset === 'custom'
            ? listStyle.marker.horizontalShift || 0
            : 0
        )};
        width: ${toCssValue(unorderedMarkerWidth)};
        ${markerShape
          ? css`
              background: ${markerShape.preset === 'circle'
                ? 'transparent'
                : markerColor};
              border: ${markerShape.preset === 'circle'
                ? `${toY(markerShape.strokeWidth)} solid ${markerColor}`
                : '0'};
              border-radius: ${markerShape.preset === 'disc' ||
              markerShape.preset === 'circle'
                ? '50%'
                : '0'};
              box-sizing: border-box;
              content: '';
              height: ${toY(markerShape.size)};
              inset-block-start: ${toY(markerShape.offset)};
              line-height: 0;
            `
          : null}
      }

      ol {
        counter-reset: list-item;
      }

      ol > li::before {
        content: counter(list-item) '.';
        text-align: end;
      }

      li.text-list-render-item::before {
        content: none;
      }
    `;
  }}
`;
